import { defineStore } from 'pinia'
import { ApiClient } from '@/services/api-client'

/**
 * ── P13-V5A-R5 · ADVERSARIAL REPAIR · A CACHED FIGURE BELONGS TO THE ACTOR WHO ACQUIRED IT ──
 *
 * Found by the adversarial pass (§18) rather than by the implementation sweep, and that is the
 * kind of leak the pass exists for: nothing here is fabricated. Every number in this cache was
 * measured, correctly scoped and truthfully reported. It was simply reported to SOMEBODY ELSE.
 *
 * THE DEFECT. `data` was a per-administration cache with no owner, no reset and no acquisition
 * epoch, and `fetchMetrics` re-threw on failure WITHOUT clearing the entry it had failed to
 * refresh. Pinia is not destroyed by an in-SPA logout — `clearSessionState` (stores/auth.ts:270)
 * clears the token, the user and the socket and touches no reporting store — and
 * `ExecutiveReports` restores its administration selection from `localStorage`, a key that
 * survives too. So:
 *
 *     actor A views administration 7 → logs out → actor B logs in (no page reload)
 *     → ExecutiveReports mounts, restores selection 7 from localStorage
 *     → renders adminStore.data[7] — A's figures — as B's current report
 *     → B's own fetch is refused, the catch re-throws, and the cached payload SURVIVES the refusal.
 *
 * A denial was therefore answered with the previous actor's numbers. That is the parallel truth
 * authority §3 forbids, arriving through cache lifetime instead of through fabrication.
 *
 * THE REPAIR is modelled on the acquisition epoch R3 introduced in `stores/realtime-analytics.ts`
 * rather than invented here: the cache records the identity that filled it. `claimFor(userId)`
 * runs before the surface renders, and anything acquired under a different owner is dropped
 * first. A failed read now also clears the entry it could not refresh, so a refusal can never be
 * served a stale success.
 *
 * FRESHNESS TRAVELS WITH THE DATA. `lastUpdated` used to live in the component, so it reset on
 * every mount while the store-global figures survived: re-entering the view republished the
 * previous acquisition with its "Actualizado" chip missing — stale data presented as current,
 * with the one signal that would have exposed it removed. It is held per administration here,
 * written only on success, and cleared with the data it describes.
 */
export interface AdminMetricsState {
  loading: boolean
  error: string | null
  data: Record<number, any>
  /** Acquisition timestamp per administration; same lifetime as the payload it describes. */
  updatedAt: Record<number, number>
  overview: any[]
  /** The user id that acquired everything currently cached. `null` = nothing acquired yet. */
  ownerUserId: number | string | null
}

export const useAdminMetricsStore = defineStore('admin-metrics', {
  state: (): AdminMetricsState => ({
    loading: false,
    error: null,
    data: {},
    updatedAt: {},
    overview: [],
    ownerUserId: null
  }),
  actions: {
    /** Drop everything acquired under a previous owner. */
    reset() {
      this.data = {}
      this.updatedAt = {}
      this.overview = []
      this.error = null
      this.loading = false
      this.ownerUserId = null
    },

    /**
     * Declare who is about to read. Anything acquired by a different identity is dropped before
     * it can be rendered. Call this BEFORE the first read of a mount.
     */
    claimFor(userId: number | string | null | undefined) {
      const next = userId ?? null
      if (this.ownerUserId !== null && this.ownerUserId !== next) this.reset()
      this.ownerUserId = next
    },

    async fetchMetrics(adminId: number, opts: { includeTrend?: boolean; windowMinutes?: number } = {}) {
      this.loading = true
      this.error = null
      try {
        const params: any = {}
        if (opts.includeTrend) params.include_trend = true
        if (opts.windowMinutes) params.window_minutes = opts.windowMinutes
        const res = await ApiClient.get(`/administrations/${adminId}/metrics`, params)
        // ApiClient.get wraps as { data, ... } but backend returns { success, data }
        const payload: any = (res as any)?.data?.data || (res as any)?.data || res
        this.data[adminId] = payload
        this.updatedAt[adminId] = Date.now()
        return payload
      } catch (e: any) {
        this.error = e?.message || 'Error cargando métricas'
        // A read that did not succeed must not leave the previous answer standing in for it.
        delete this.data[adminId]
        delete this.updatedAt[adminId]
        throw e
      } finally {
        this.loading = false
      }
    },

    async fetchOverview() {
      this.loading = true
      this.error = null
      try {
        const res = await ApiClient.get(`/administrations/metrics/overview`)
        const payload: any = (res as any)?.data?.data || (res as any)?.data || res
        this.overview = payload?.administrations || []
        return this.overview
      } catch (e: any) {
        this.error = e?.message || 'Error cargando overview'
        this.overview = []
        throw e
      } finally {
        this.loading = false
      }
    }
  }
})
